
import { createContext, ReactNode, useContext } from 'react';
import api from '../services/api';

interface BoxType {
  dun14: string;
  description: string;
  amount: number;
}

interface BoxTypesProvidersProps {
  children: ReactNode;
}

interface ResponseProps {
  success: boolean;
  message: string;
}

interface ResponsePropsWithData {
  success: boolean;
  message: string;
  data: any;
}

interface BoxStockFilter {
  type: string;
  startDate: string;
  endDate: string;
  box_description: string;
}

interface BatchId {
  id: string;
}

interface BatchData {
  batch: string;
  production_date: string;
  validity_date: string;
  dun14: string;
  boxes: BatchId[];
};

type BoxTypeInput = Omit<BoxType, 'id'>

interface BoxTypesContextData {
  listBoxTypes: () => Promise<ResponsePropsWithData>;
  createBoxType: (boxType: BoxTypeInput) => Promise<ResponseProps>;
  updateBoxType: (dun14: string, description: string, amount: number) => Promise<ResponseProps>;
  deleteBoxType: (dun14: string) => Promise<ResponseProps>;
  listBoxStockMovements: (filter: BoxStockFilter) => Promise<ResponsePropsWithData>;
  listBoxesCurrentStock: () => Promise<ResponsePropsWithData>;
  updateBatch: (batchData: BatchData) => Promise<ResponseProps>;
  getImageURL: (boxId: string) => Promise<ResponsePropsWithData>;
  deleteBox: (boxId: string) => Promise<ResponseProps>;
}

const BoxTypesContext = createContext<BoxTypesContextData>(
  {} as BoxTypesContextData
);

export function BoxTypesProvider({ children }: BoxTypesProvidersProps) {
  async function listBoxTypes() {
    const response = await api.get('/box-types');

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }

    return {
      success: false,
      message: "Erro ao listar tipos de caixa.",
      data: []
    };
  }

  async function createBoxType(boxTypeInput: BoxTypeInput) {
    const response = await api.post('/box-types', boxTypeInput);

    if (response.status === 201 && !response.data.error) {
      return {
        success: true,
        message: "Tipo de caixa adicionado com sucesso!"
      };
    }

    return {
      success: false,
      message: response?.data.error || "Erro ao adicionar tipo de caixa."
    };
  }

  async function updateBoxType(dun14: string, description: string, amount: number) {
    const response = await api.put(`/box-types/${dun14}`, { description, amount });
    if (response.status === 200) {
      return {
        success: true,
        message: "Tipo de caixa editado com sucesso!"
      };
    }

    return {
      success: false,
      message: "Erro ao editar tipo de caixa."
    };
  }

  async function deleteBoxType(dun14: string) {
    const response = await api.delete(`/box-types/${dun14}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "Tipo de caixa removido com sucesso!"
      };
    }

    return {
      success: false,
      message: "Erro ao remover tipo de caixa."
    };
  }

  async function listBoxStockMovements(filter: BoxStockFilter) {
    const response = await api.get('/box-stock-movements', { params: filter });

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }

    return {
      success: false,
      message: "Erro ao listar os movimentos do estoque da câmara frigorífica.",
      data: []
    };
  }

  async function listBoxesCurrentStock() {
    const response = await api.get('/boxes-current-stock');

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }

    return {
      success: false,
      message: "Erro ao listar o estoque da câmara frigorífica.",
      data: []
    };
  }

  async function updateBatch(batchData: BatchData) {
    const response = await api.put('/box-stock-movements', batchData);

    if (response.status === 200) {
      return {
        success: true,
        message: "Dados do lote ou de uma caixa atualizado com sucesso!"
      };
    }

    return {
      success: false,
      message: "Erro ao editar lote ou caixa."
    };
  }

  async function getImageURL(boxId: string) {
    const response = await api.get(`/boxes/${boxId}/image`);

    if (response.status === 200) {
      return {
        success: true,
        message: "",
        data: response.data
      };
    }

    return {
      success: false,
      message: "Erro ao carregar a imagem.",
      data: []
    };
  }

  async function deleteBox(boxId: string) {
    const response = await api.delete(`/box-stock-movements/${boxId}`);

    if (response.status === 200) {
      return {
        success: true,
        message: "Caixa removida com sucesso!"
      };
    }

    return {
      success: false,
      message: "Erro ao remover caixa."
    };
  }

  return (
    <BoxTypesContext.Provider value={{ listBoxTypes, createBoxType, updateBoxType, deleteBoxType, listBoxStockMovements, listBoxesCurrentStock, updateBatch, getImageURL, deleteBox }}>
      {children}
    </BoxTypesContext.Provider>
  );
}

export function UseBoxShipments() {
  const context = useContext(BoxTypesContext);

  return context;
}
